import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface PageVisit {
    id: number;
    userId: number | null;
    userEmail: string | null;
    userRole: number | null;
    sessionId: string | null;
    url: string | null;
    route: string | null;
    urlWithoutQuery: string | null;
    fullUrl: string | null;
    referrer: string | null;
    userAgent: string | null;
    ipAddress: string | null;
    pageTitle: string | null;
    createdAt: string;
}

export interface PageVisitListResponse {
    total: number;
    page: number;
    pageSize: number;
    items: PageVisit[];
}

export interface PageVisitSummaryItem {
    year: number;
    month: number | null;
    day: number | null;
    total: number;
    uniqueUsers: number;
    uniqueSessions: number;
}

export interface PageVisitSummaryResponse {
    groupBy: 'day' | 'month' | 'year';
    items: PageVisitSummaryItem[];
}

export interface TopRouteItem {
    route: string;
    total: number;
    uniqueUsers: number;
}

export interface TopUserItem {
    userId: number;
    email: string;
    total: number;
    uniqueRoutes: number;
}

export interface PageVisitExclusion {
    id: number;
    userId: number;
    userEmail: string | null;
    userName?: string | null;
    userRole?: number | null;
    note: string | null;
    createdAt: string;
}

export interface PageVisitExclusionListResponse {
    items: PageVisitExclusion[];
}

export interface PageVisitListFilters {
    from?: string | null;
    to?: string | null;
    userId?: number | null;
    route?: string | null;
    email?: string | null;
    page?: number;
    pageSize?: number;
}

// ---------- Session aggregates (Phase 2 /sessions endpoint) ----------

export interface SessionSummary {
    sessionId: string;
    userId: number | null;
    userEmail: string | null;
    userRole: number | null;
    startAt: string;
    endAt: string;
    durationMs: number;
    pageCount: number;
    uniqueRouteCount: number;
    entryRoute: string | null;
    exitRoute: string | null;
}

export interface SessionListResponse {
    total: number;
    page: number;
    pageSize: number;
    items: SessionSummary[];
}

export interface SessionListFilters {
    from?: string | null;
    to?: string | null;
    userId?: number | null;
    email?: string | null;
    search?: string | null;
    sort?: 'start' | 'duration' | 'pages' | 'user';
    dir?: 'asc' | 'desc';
    page?: number;
    pageSize?: number;
}

export interface SessionVisitsResponse {
    sessionId: string;
    total: number;
    items: PageVisit[];
}

// ---------- Flow / Sankey (Phase 3) ----------

export interface FlowEdge {
    from: string;
    to: string;
    count: number;
    uniqueUsers: number;
    uniqueSessions: number;
}

export interface FlowResponse {
    totalTransitions: number;
    totalEdgesAvailable: number;
    returned: number;
    minCount: number;
    limit: number;
    items: FlowEdge[];
}

export interface FlowFilters {
    from?: string | null;
    to?: string | null;
    role?: number | null;
    minCount?: number | null;
    limit?: number | null;
}

// ---------- Hour heatmap (Phase 3b) ----------

export interface HeatmapCell {
    dow: number;  // 0 = Sunday .. 6 = Saturday
    hour: number; // 0 .. 23
    count: number;
}

export interface HeatmapResponse {
    total: number;
    max: number;
    cells: HeatmapCell[];
}

export interface HeatmapFilters {
    from?: string | null;
    to?: string | null;
    role?: number | null;
}

// ---------- Funnel (Phase 3c) ----------

export interface FunnelStep {
    step: number;
    name: string;
    pattern: string;
    sessions: number;
    pctOfTotal: number;
    pctOfPrev: number;
}

export interface FunnelResponse {
    preset: string;
    totalSessions: number;
    steps: FunnelStep[];
}

export interface FunnelFilters {
    preset: string;
    from?: string | null;
    to?: string | null;
    role?: number | null;
}

const API_URL = environment.API_BASE_URL + '/api/PageVisit';

@Injectable({
    providedIn: 'root',
})
export class PageVisitService {
    constructor(private http: HttpClient) { }

    getList(filters: PageVisitListFilters): Observable<PageVisitListResponse> {
        const params = this.buildParams({
            from: filters.from,
            to: filters.to,
            userId: filters.userId,
            route: filters.route,
            email: filters.email,
            page: filters.page,
            pageSize: filters.pageSize,
        });
        return this.http.get<PageVisitListResponse>(API_URL, {
            headers: this.buildAuthHeaders(),
            params,
        });
    }

    getSummary(
        groupBy: 'day' | 'month' | 'year',
        from?: string | null,
        to?: string | null
    ): Observable<PageVisitSummaryResponse> {
        const params = this.buildParams({ groupBy, from, to });
        return this.http.get<PageVisitSummaryResponse>(API_URL + '/summary', {
            headers: this.buildAuthHeaders(),
            params,
        });
    }

    getTopRoutes(
        from?: string | null,
        to?: string | null,
        limit = 10
    ): Observable<{ items: TopRouteItem[] }> {
        const params = this.buildParams({ from, to, limit });
        return this.http.get<{ items: TopRouteItem[] }>(API_URL + '/top-routes', {
            headers: this.buildAuthHeaders(),
            params,
        });
    }

    getTopUsers(
        from?: string | null,
        to?: string | null,
        limit = 10
    ): Observable<{ items: TopUserItem[] }> {
        const params = this.buildParams({ from, to, limit });
        return this.http.get<{ items: TopUserItem[] }>(API_URL + '/top-users', {
            headers: this.buildAuthHeaders(),
            params,
        });
    }

    // ---------- Sessions (Phase 2) ----------

    /**
     * Server-aggregated session list (one row per sessionId). Replaces the
     * Phase 1 client-side grouping of up to 1000 raw visits.
     */
    getSessionList(filters: SessionListFilters): Observable<SessionListResponse> {
        const params = this.buildParams({
            from: filters.from,
            to: filters.to,
            userId: filters.userId,
            email: filters.email,
            search: filters.search,
            sort: filters.sort,
            dir: filters.dir,
            page: filters.page,
            pageSize: filters.pageSize,
        });
        return this.http.get<SessionListResponse>(API_URL + '/sessions', {
            headers: this.buildAuthHeaders(),
            params,
        });
    }

    /**
     * All visits for a single session, ordered oldest-first. Used to render
     * the waterfall when a session row is expanded.
     */
    getSessionVisits(sessionId: string): Observable<SessionVisitsResponse> {
        return this.http.get<SessionVisitsResponse>(
            `${API_URL}/sessions/${encodeURIComponent(sessionId)}`,
            { headers: this.buildAuthHeaders() }
        );
    }

    /**
     * Aggregated route-to-route transitions across sessions — drives the
     * Sankey flow diagram on the "Flow" tab.
     */
    getFlow(filters: FlowFilters): Observable<FlowResponse> {
        const params = this.buildParams({
            from: filters.from,
            to: filters.to,
            role: filters.role,
            minCount: filters.minCount,
            limit: filters.limit,
        });
        return this.http.get<FlowResponse>(API_URL + '/flow', {
            headers: this.buildAuthHeaders(),
            params,
        });
    }

    /**
     * 7×24 hour-of-day × day-of-week visit counts for the Heatmap tab.
     */
    getHourHeatmap(filters: HeatmapFilters): Observable<HeatmapResponse> {
        const params = this.buildParams({
            from: filters.from,
            to: filters.to,
            role: filters.role,
        });
        return this.http.get<HeatmapResponse>(API_URL + '/hour-heatmap', {
            headers: this.buildAuthHeaders(),
            params,
        });
    }

    /**
     * Funnel conversion for a named preset flow. Each step is a fixed route
     * pattern defined on the server. Session must hit steps in order (any
     * detour pages in between are allowed).
     */
    getFunnel(filters: FunnelFilters): Observable<FunnelResponse> {
        const params = this.buildParams({
            preset: filters.preset,
            from: filters.from,
            to: filters.to,
            role: filters.role,
        });
        return this.http.get<FunnelResponse>(API_URL + '/funnel', {
            headers: this.buildAuthHeaders(),
            params,
        });
    }

    // ---------- Delete ----------

    deleteVisit(id: number): Observable<{ id: number }> {
        return this.http.delete<{ id: number }>(`${API_URL}/${id}`, {
            headers: this.buildAuthHeaders(),
        });
    }

    deleteVisitsByFilter(filters: PageVisitListFilters): Observable<{ removed: number }> {
        const params = this.buildParams({
            from: filters.from,
            to: filters.to,
            userId: filters.userId,
            route: filters.route,
            email: filters.email,
        });
        return this.http.delete<{ removed: number }>(API_URL, {
            headers: this.buildAuthHeaders(),
            params,
        });
    }

    deleteVisitsByIds(ids: number[]): Observable<{ removed: number }> {
        return this.http.post<{ removed: number }>(
            API_URL + '/delete-bulk',
            { ids },
            { headers: this.buildAuthHeaders() }
        );
    }

    // ---------- Exclusions ----------

    getExclusions(): Observable<PageVisitExclusionListResponse> {
        return this.http.get<PageVisitExclusionListResponse>(API_URL + '/exclusions', {
            headers: this.buildAuthHeaders(),
        });
    }

    addExclusion(userId: number, note?: string | null): Observable<PageVisitExclusion> {
        return this.http.post<PageVisitExclusion>(
            API_URL + '/exclusions',
            { userId, note: note || null },
            { headers: this.buildAuthHeaders() }
        );
    }

    removeExclusion(id: number): Observable<{ id: number }> {
        return this.http.delete<{ id: number }>(`${API_URL}/exclusions/${id}`, {
            headers: this.buildAuthHeaders(),
        });
    }

    private buildParams(obj: { [key: string]: any }): HttpParams {
        let params = new HttpParams();
        Object.keys(obj).forEach((key) => {
            const value = obj[key];
            if (value !== null && value !== undefined && value !== '') {
                params = params.set(key, String(value));
            }
        });
        return params;
    }

    private buildAuthHeaders(): HttpHeaders {
        let headers = new HttpHeaders().set('Accept', 'application/json');
        try {
            const token = localStorage.getItem(environment.authTokenKey);
            if (token) {
                headers = headers.set('Authorization', `Bearer ${token}`);
            }
        } catch {
            // no-op
        }
        return headers;
    }
}
